import { useState, useEffect } from 'react';

import { getExperimentByKey } from './helpers';
import sleep from '../../util/sleep';
import canUseDOM from '../../util/can-use-dom';

/**
 * Attemps to get the variant index from Google Optimize. Checks for
 * existence of window.google_optimize as it might be injected asynchronously
 * in the app.
 *
 * @param {string} experimentId The experiment id.
 * @param {number} [attempts=3] How many attempts to make before giving up.
 * @returns {Promise<*|undefined|Promise<*|*|*>>}
 */
const attemptFetchVariantIndex = async (experimentId, attempts = 3) => {
  if (!experimentId || attempts === 0) {
    return undefined;
  }

  // Google Optimize returns a zero-indexed "numeric string" corresponding
  // to the variant that should be seen by the user. i.e. if it selects
  // the first variant this will return '0', for the second one '1', and so on.
  const variantIndex = window?.google_optimize?.get(experimentId);

  if (variantIndex == null) {
    await sleep(300);
    return attemptFetchVariantIndex(experimentId, attempts - 1);
  }

  return parseInt(variantIndex, 10);
};

/**
 * Given an EXPERIMENT_KEY it attempts to fetch the experiment's variant.
 *
 * @param {EXPERIMENT_KEY} experimentKey The experiment's key.
 * @param {string} [defaultVariant] The default variant to use.
 * @returns {string}
 */
const useExperimentVariant = (experimentKey, defaultVariant) => {
  const [variantIndex, setVariantIndex] = useState();
  const [variant, setVariant] = useState(defaultVariant);
  const experiment = getExperimentByKey(experimentKey);

  const fetchVariantIndex = async () => {
    // Run this only on the client side as (of course) Google Optimize
    // is not available on the server.
    if (canUseDOM) {
      const index = await attemptFetchVariantIndex(experiment?.id);
      setVariantIndex(index);
    }
  };

  fetchVariantIndex();

  useEffect(() => {
    if (!experiment) {
      return;
    }

    setVariant(experiment.variants[variantIndex] || defaultVariant);
  }, [variantIndex]);

  return variant;
};

export default useExperimentVariant;
